import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { ClientHttpRequester } from "@smarterservices/client-http-requester";
import { EVENTS, MediaAgent } from "@smarterservices/media-agent";

/**
 * Custom hook encapsulating chat logic and state management.
 * 
 * This hook provides functionality for managing chat messages, scrolling to specific messages,
 * and tracking session time. It also handles sending new messages and updating the chat state.
 * 
 * @param {object} params - Configuration parameters from ReactChat props.
 * @param {string} params.roomId - Unique identifier for the chat room.
 * @param {string} params.identity - Unique identifier for the user.
 * @param {string} params.token - Authentication token for the user.
 * @param {string} params.referenceId - Reference ID for the chat session.
 * @param {string} params.apiUrl - Base URL for the API.
 * @param {string} params.websocketUrl - URL for the WebSocket connection.
 * @param {string} params.displayName - Display name for the user.
 * @param {object} params.connectionBundle - Optional connection bundle for MediaAgent.
 * @param {object} params.mediaAgent - Optional existing MediaAgent instance to use instead of creating a new one.
 * @param {object} params.roomEventEmitter - Event emitter for the chat room (deprecated, use mediaAgent instead).
 * @param {number|null} params.startTime - Start time of the chat session (milliseconds since epoch).
 * @param {function} params.onReady - Callback fired when chat is fully initialized.
 * @param {function} params.onNewMessage - Callback fired when a new message is received.
 */
export default function useChat({
  roomId,
  identity,
  token,
  referenceId,
  apiUrl,
  websocketUrl,
  displayName,
  connectionBundle,
  mediaAgent,
  roomEventEmitter,
  startTime = null, // Default to null if not provided
  onReady = null, // Callback fired when chat is fully initialized
  onNewMessage = null // Callback fired when a new message is received
}) {
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [sendError, setSendError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [showNewMessageButton, setShowNewMessageButton] = useState(false);
  const [lastMessageCountSeen, setLastMessageCountSeen] = useState(0);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const isAtBottomRef = useRef(true);
  const initialLoadRef = useRef(true);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const messageRefs = useRef({});

  // Initialize HTTP client for REST API
  const requesterConfig = {
    apiUrl,
    retry: { enabled: true },
  };
  const api = ClientHttpRequester(requesterConfig);

  /**
   * Calculate session time for a message based on chat start time
   * Memoized to prevent unnecessary recalculations
   * 
   * @param {string|number} messageTimestamp - Timestamp of the message (milliseconds since epoch)
   * @returns {number|null} - Session time in seconds or null if no start time available
   */
  const calculateSessionTime = useCallback((messageTimestamp) => {
    if (!startTime || !messageTimestamp) return null;
    
    const messageTime = new Date(parseInt(messageTimestamp, 10));
    const chatStartTime = new Date(startTime);
    
    // Calculate difference in seconds
    const diffSeconds = Math.floor((messageTime - chatStartTime) / 1000);
    return diffSeconds > 0 ? diffSeconds : 0;
  }, [startTime]);

  /**
   * Process messages to add sessionTime property to each message
   * Memoized to prevent recalculating session times on every render
   * 
   * @param {Array} msgs - Array of message objects
   * @returns {Array} - Messages with sessionTime added
   */
  const processMessages = useCallback((msgs) => {
    return msgs.map(msg => ({
      ...msg,
      sessionTime: calculateSessionTime(msg.createdDate)
    }));
  }, [calculateSessionTime]);

  // Fetch chat history
  const getChatMessages = async () => {
    try {
      const url = `/v1/rooms/${roomId}/messages`;
      const headers = { token };
      const response = await api.get(url, headers);
      return processMessages(response.data);
    } catch (error) {
      console.error('Error getting chat messages:', error);
      throw error;
    }
  };

  // Send a new message
  const sendChatMessage = async (message) => {
    try {
      const url = `/v1/rooms/${roomId}/messages`;
      const headers = { token };
      const payload = { message, identity, referenceId, displayName };
      await api.post(url, headers, payload);
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  };

  /**
   * Scrolls to the bottom of the message list, preferring the end anchor if available.
   * Memoized to provide stable reference for child components
   * 
   * @param {boolean} instant - Whether to scroll instantly (true) or smoothly (false)
   */
  const scrollToBottom = useCallback((instant = false) => {
    // Scroll via end ref if present
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: instant ? 'auto' : 'smooth' 
      });
    } else {
      // Fallback to container scroll
      const container = chatContainerRef.current?.ref;
      if (container) {
        container.scrollTo({ 
          top: container.scrollHeight, 
          behavior: instant ? 'auto' : 'smooth' 
        });
      }
    }
    setLastMessageCountSeen(messages.length);
    isAtBottomRef.current = true;
    setShowNewMessageButton(false);
    
    // Clear any highlighted message
    setHighlightedMessageId(null);
  }, [messages.length]);

  /**
   * Scrolls to a message with a specific session time
   * Memoized to provide stable reference for child components
   * 
   * This function finds either:
   * 1. A message with exactly the target session time (exact match)
   * 2. The most recent message before the target time (closest match)
   * 
   * This is useful for synchronizing chat with video or audio playback.
   * 
   * @param {number} targetSessionTime - The session time to scroll to (in seconds)
   * @returns {boolean} - Whether a matching message was found and scrolled to (true = exact match)
   */
  const scrollToMessageBySessionTime = useCallback((targetSessionTime) => {
    if (!targetSessionTime || typeof targetSessionTime !== 'number') {
      return false;
    }

    // Find either:
    // 1. A message with exactly the target session time, or
    // 2. The most recent message before the target time
    let exactMatch = false;
    let targetMessage = null;
    let mostRecentMessage = null;
    let mostRecentSessionTime = -1;

    // First pass: look for exact match or build most recent message
    for (const msg of messages) {
      // Skip messages without session time
      if (msg.sessionTime === undefined || msg.sessionTime === null) {
        continue;
      }
      
      // Case 1: Exact match
      if (msg.sessionTime === targetSessionTime) {
        targetMessage = msg;
        exactMatch = true;
        break;
      }
      
      // Case 2: Message is before target time and is more recent than current most recent
      if (msg.sessionTime < targetSessionTime && msg.sessionTime > mostRecentSessionTime) {
        mostRecentMessage = msg;
        mostRecentSessionTime = msg.sessionTime;
      }
    }

    // If the closest message is more than 30s away, clear all messages
    if (
      targetMessage &&
      Math.abs(targetMessage.sessionTime - targetSessionTime) > 30
    ) {
      setHighlightedMessageId(null); // Also clear highlight
      return false;
    }

    // If no exact match found, use the most recent message before target time
    if (!exactMatch && mostRecentMessage) {
      targetMessage = mostRecentMessage;
    }

    // If no message found at all (either exact or most recent), return false
    if (!targetMessage) {
      if (highlightedMessageId) {
        setHighlightedMessageId(null);
      }
      return false;
    }

    // Scroll to the target message
    const msgId = targetMessage.sid;
    const msgRef = messageRefs.current[msgId];

    if (msgRef) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        // Set highlighted message ID
        setHighlightedMessageId(msgId);
        
        // Scroll to the message
        msgRef.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      });
      
      return exactMatch;
    }
    
    // Clear highlight if we couldn't find a message ref
    if (highlightedMessageId) {
      setHighlightedMessageId(null);
    }
    
    return false;
  }, [messages, highlightedMessageId]);

  /**
   * Scroll event handler
   * Memoized to prevent unnecessary re-renders when passed to child components
   */
  const handleScroll = useCallback(() => {
    const container = chatContainerRef.current?.ref;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 20;
    setAutoScrollEnabled(atBottom);

    if (atBottom) {
      setLastMessageCountSeen(messages.length);
      setShowNewMessageButton(false);
    } else if (messages.length > lastMessageCountSeen) {
      setShowNewMessageButton(true);
    }
  }, [messages.length, lastMessageCountSeen]);

  /**
   * Format timestamps for display
   * Memoized to provide stable reference for child components
   */
  const formatTimestamp = useCallback((timestamp) => {
    const ms = parseInt(timestamp, 10);
    let timeString = new Date(ms).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
    return timeString.replace(/^0/, '');
  }, []);

  /**
   * Message send handler
   * Memoized to provide stable reference for child components
   */
  const handleSubmit = useCallback(async (message) => {
    // Skip if empty message or already submitting
    if (!message || isSubmitting) return;

    setIsSubmitting(true);
    setSendError('');
    try {
      await sendChatMessage(message);
      return true;
      // No need to clear messageText state since we're using uncontrolled component now
    } catch {
      setSendError('Failed to send message.');
      return false;
    } finally {
      setTimeout(() => setIsSubmitting(false), 500);
    }
  }, [isSubmitting, roomId, token, identity, referenceId, displayName]);

  // Scroll effect on new messages
  useEffect(() => {
    if (autoScrollEnabled) {
      if (initialLoadRef.current) {
        // Use instant scroll for initial load
        initialLoadRef.current = false;
        requestAnimationFrame(() => scrollToBottom(true));
      } else {
        requestAnimationFrame(() => scrollToBottom(false));
      }
    } else if (messages.length > lastMessageCountSeen) {
      setShowNewMessageButton(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, autoScrollEnabled]);

  // Initialize chat: load history & subscribe
  useEffect(() => {
    const init = async () => {
      const prev = await getChatMessages();
      setMessages(prev);
      
      // Scroll to bottom after messages are loaded and rendered
      // Use multiple attempts to ensure scroll happens even with slow-loading content
      const scrollAttempts = [100, 300, 500];
      scrollAttempts.forEach(delay => {
        setTimeout(() => {
          scrollToBottom(true);
        }, delay);
      });

      let emitter = null;
      
      // Priority: mediaAgent > roomEventEmitter > create new MediaAgent
      if (mediaAgent) {
        // Use existing MediaAgent instance (already joined to room)
        emitter = mediaAgent.getEventEmitter();

      } else if (roomEventEmitter) {
        // Use existing event emitter (legacy support)
        emitter = roomEventEmitter;
      } else {
        // Create new MediaAgent instance and join room
        const mediaAgentConfig = connectionBundle || {
          apiUrl,
          roomId,
          type: 'chat-only',
          identity: `${identity}chat`,
          token,
          websocketUrl
        };
        
        const client = await MediaAgent(mediaAgentConfig);
        await client.joinRoom();
        emitter = client.getEventEmitter();
      }
      
      if (!emitter) throw new Error('Unable to get event emitter from MediaAgent');
      
      emitter.on(EVENTS.CHAT_MESSAGE_ADDED, ({ payload }) => {
        if (payload) {
          // Add sessionTime to the new message
          const processedPayload = {
            ...payload,
            sessionTime: calculateSessionTime(payload.createdDate)
          };
          setMessages((prev) => [...prev, processedPayload]);
          
          // Call onNewMessage callback if provided
          if (onNewMessage && typeof onNewMessage === 'function') {
            onNewMessage(processedPayload);
          }
        }
      });
      
      // Call onReady callback after initialization is complete
      if (onReady && typeof onReady === 'function') {
        // Use the last scroll attempt delay to ensure everything is ready
        setTimeout(() => {
          onReady({
            messageCount: prev.length,
            connected: true
          });
        }, 500);
      }
    };
    init();
/*
    // Periodic fetch fallback
    const fetchLoop = async () => {
      try {
        const msgs = await getChatMessages();
        setMessages(msgs);
      } catch (err) {
        console.error('Error fetching chat messages:', err);
      }
    };
    fetchLoop();
    */
  }, []);

  return {
    messages,
    messageText,
    setMessageText,
    sendError,
    isSubmitting,
    showNewMessageButton,
    scrollToBottom,
    scrollToMessageBySessionTime,
    handleScroll,
    formatTimestamp,
    handleSubmit,
    chatContainerRef,
    messagesEndRef,
    messageRefs,
    highlightedMessageId,
    setHighlightedMessageId,
    identity,
    calculateSessionTime
  };
}
